import { useEffect } from "react"
import { Spin, Result, Layout } from "@mimber/ui-web"
import { useAuth } from "@/core/contexts"
import { parseAuthUserName } from "@mimber/auth"
import {
  useLogin,
  useRegister,
  useRecordLoginEvent,
  useConfig,
} from "@mimber/client/mimber-organization"
import * as Sentry from "@sentry/react"
import { AccountStatusGate } from "@/components/gates/AccountStatusGate"
import { BetaAccessGate } from "@/components/gates/BetaAccessGate"
import type { UserGateProps } from "./UserGate.types"

const { Content } = Layout

const INVITE_CODE_KEY = "mimber_invite_code"

/**
 * UserGate - Handles backend user registration and login flow
 *
 * This gate assumes the user is already authenticated with Cognito (via AuthGate).
 * It handles:
 * 1. Attempting to login to backend API with Cognito identityId
 * 2. If user doesn't exist (404), automatically registers the user
 * 3. Shows appropriate loading and error states
 *
 * Flow:
 * - On mount with authenticated Cognito user, attempt login
 * - If login returns 404, attempt registration with email from Cognito
 * - If login or registration succeeds, render children
 * - If errors occur, show retry UI
 */
export const UserGate: React.FunctionComponent<UserGateProps> = ({
  children,
}) => {
  const { user } = useAuth()

  const { mutate: recordLoginEvent } = useRecordLoginEvent()

  const { data: configResponse } = useConfig()
  const config = configResponse?.data

  // Initialize register mutation first so it can be referenced in login's onError
  const registerMutation = useRegister({
    onSuccess: (response) => {
      if (response.data) {
        Sentry.setUser({
          id: response.data.uuid,
        })
        // Only record login event if user won't be beta-gated
        // (beta guard blocks authenticated endpoints for users without waitlist access)
        if (!config?.publicBetaMode || response.data.waitlist) {
          recordLoginEvent()
        }
        // Clear invite code from localStorage after successful registration
        localStorage.removeItem(INVITE_CODE_KEY)
      }
    },
  })

  const loginMutation = useLogin({
    onSuccess: (response) => {
      if (response.data) {
        Sentry.setUser({
          id: response.data.uuid,
        })
        // Only record login event if user won't be beta-gated
        // (beta guard blocks authenticated endpoints for users without waitlist access)
        if (!config?.publicBetaMode || response.data.waitlist) {
          recordLoginEvent()
        }
      }
    },
    onError: (error: any) => {
      console.log("=== UserGate Error Debug ===")
      console.log("Login error:", error)
      console.log("Error response status:", error?.response?.status)
      console.log("Register mutation state:", {
        isSuccess: registerMutation.isSuccess,
        isPending: registerMutation.isPending,
        isError: registerMutation.isError,
      })
      console.log("Full user object:", user)
      console.log("User email:", user?.email)
      console.log("User sub:", user?.sub)
      console.log("===========================")

      // Handle 404 with auto-registration
      // Axios errors have status at error.response.status
      if (
        error?.response?.status === 404 &&
        !registerMutation.isSuccess &&
        !registerMutation.isPending &&
        !registerMutation.isError
      ) {
        // If no email available from Cognito, we need to prompt user
        // For now, show an error message
        if (!user?.email) {
          console.error(
            "Cannot auto-register: email not available from Cognito user"
          )
          // TODO: Show modal to collect email or update Cognito configuration
          return
        }

        const parsedName = parseAuthUserName(user)

        if (!parsedName) {
          console.error(
            "Cannot auto-register: first and last name not available from Cognito user"
          )
          return
        }

        console.log("Triggering auto-registration for:", user.email)
        const inviteCode = localStorage.getItem(INVITE_CODE_KEY) ?? undefined
        registerMutation.mutate({
          email: user.email,
          firstName: parsedName.firstName,
          lastName: parsedName.lastName,
          inviteCode,
        })
      }
    },
  })

  // Auto-trigger login when Cognito authentication completes
  useEffect(() => {
    if (
      user?.sub &&
      !loginMutation.data &&
      !loginMutation.isPending &&
      !loginMutation.isError &&
      !registerMutation.isPending &&
      !registerMutation.isSuccess
    ) {
      loginMutation.mutate({})
    }
  }, [
    user?.sub,
    loginMutation.data,
    loginMutation.isPending,
    loginMutation.isError,
    loginMutation.mutate,
    registerMutation.isPending,
    registerMutation.isSuccess,
  ])

  // Determine overall loading state
  const isLoading = loginMutation.isPending || registerMutation.isPending

  // Determine if user is fully registered/logged in
  const isUserReady = loginMutation.isSuccess || registerMutation.isSuccess

  // Determine error state (only if both have failed)
  const hasLoginError = loginMutation.isError && !registerMutation.isSuccess
  const hasRegisterError = registerMutation.isError
  const isError = hasLoginError || hasRegisterError
  const error = hasLoginError
    ? loginMutation.error
    : hasRegisterError
      ? registerMutation.error
      : null

  // Show loading while checking user status or registering
  if (isLoading) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--color-background)",
          }}
        >
          <div className="flex flex-col items-center text-center">
            <Spin size="large" />
            <p className="mt-4 text-muted-foreground">
              {registerMutation.isPending
                ? "Setting up your account..."
                : "Loading your account..."}
            </p>
          </div>
        </Content>
      </Layout>
    )
  }

  // Show error if both login and registration failed
  if (isError && error) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: "var(--color-background)",
          }}
        >
          <div className="max-w-md w-full">
            <Result
              status="error"
              title="Account Setup Failed"
              subTitle={
                error?.message ||
                "Unable to set up your account. Please try again."
              }
              extra={[
                <button
                  key="retry"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  onClick={() => {
                    loginMutation.reset()
                    registerMutation.reset()
                    loginMutation.mutate({})
                  }}
                >
                  Try Again
                </button>,
              ]}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // User is ready - check account status before rendering children
  if (isUserReady) {
    const userData =
      loginMutation.data?.data ?? registerMutation.data?.data ?? null

    // Beta access gating: if publicBetaMode is enabled, only users without waitlist access are gated
    if (config?.publicBetaMode && !userData?.waitlist) {
      return (
        <BetaAccessGate
          userData={userData}
          onRetryLogin={() => {
            loginMutation.reset()
            registerMutation.reset()
            loginMutation.mutate({})
          }}
        />
      )
    }

    return (
      <AccountStatusGate
        deactivated={userData?.deactivated ?? false}
        deleted={userData?.deleted ?? null}
        deletedScheduledAt={userData?.deletedScheduledAt ?? null}
      >
        {children}
      </AccountStatusGate>
    )
  }

  // Waiting for initial load (shouldn't normally reach here)
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Content
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-background)",
        }}
      >
        <div className="flex flex-col items-center text-center">
          <Spin size="large" />
        </div>
      </Content>
    </Layout>
  )
}
